import { UsercentricsConsentService } from './UsercentricsConsentService';
import { featureFlags } from '@/store/feature-flag.store';
import { setupChatConfig } from '@/utils/chatConfig';
import chatbotConfig from '@/assets/chatbot-widget.json';
import Logger from './LoggerService';

export class ChatbotService {
    private static CATEGORY_SLUG = 'functional';
    private static isInitialized = false;
    private static isLoaded = false;
    private static unsubscribe: (() => void) | null = null;
    private static logger = new Logger('ChatbotService');

    /** Initialize service and listen for consent changes */
    public static initialize(): void {
        if (this.isInitialized) {
            return;
        }

        if (!featureFlags.getFeatureFlag('BOOKING_RMD_CHATBOT')) {
            return;
        }

        this.unsubscribe = UsercentricsConsentService.onCategoryConsentChange(
            this.CATEGORY_SLUG,
            (hasConsent: boolean) => {
                if (hasConsent) {
                    this.loadChatbot();
                } else {
                    this.removeChatbot();
                }
            }
        );

        this.isInitialized = true;
    }

    /** Cleanup consent listener */
    public static cleanup(): void {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        this.isInitialized = false;
    }

    /** Inject the Microsoft Chatbot script */
    private static loadChatbot(): void {
        if (this.isLoaded) {
            this.showChatbot();
            return;
        }

        setupChatConfig();

        const createScript = (src: string): HTMLScriptElement => {
            const s = document.createElement('script');
            s.id = chatbotConfig.id;
            s.src = src;
            s.async = true;
            Object.entries(chatbotConfig.attributes).forEach(([key, value]) => {
                s.setAttribute(key, value);
            });
            return s;
        };

        const script = createScript(chatbotConfig.primarySrc);

        // Fallback to secondary CDN if the primary one fails
        script.onerror = () => {
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
            const fallbackScript = createScript(chatbotConfig.fallbackSrc);
            document.body.appendChild(fallbackScript);
        };

        document.body.appendChild(script);
        this.isLoaded = true;
    }

    /** Remove the script and hide/remove all widget elements */
    public static removeChatbot(): void {
        const script = document.getElementById(chatbotConfig.id);
        if (script && script.parentNode) {
            script.parentNode.removeChild(script);
        }

        const anyWindow = window as any;
        if (anyWindow.Microsoft && anyWindow.Microsoft.Omnichannel && anyWindow.Microsoft.Omnichannel.LiveChatWidget && anyWindow.Microsoft.Omnichannel.LiveChatWidget.SDK) {
            try {
                anyWindow.Microsoft.Omnichannel.LiveChatWidget.SDK.closeChat();
            } catch (e) {
                this.logger.logWarning('Failed to close chat via SDK', e);
            }
        }

        const widgetSelectors = chatbotConfig.selectors;

        widgetSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            if (elements.length > 0) {
                elements.forEach(el => {
                    (el as HTMLElement).style.display = 'none';
                    if (el.parentNode) {
                        el.parentNode.removeChild(el);
                    }
                });
            }
        });

        this.isLoaded = false;
    }

    private static showChatbot(): void {
        const widgetSelectors = chatbotConfig.showSelectors;

        widgetSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(el => {
                (el as HTMLElement).style.display = 'block';
            });
        });
    }
}
